import { useState, useEffect, useRef } from 'react'
import { X, Chrome, ClipboardPaste, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { KaijuLoader } from '../KaijuLoader'
import { notify } from '../../lib/notify'

type Step = 'method' | 'auto-auth' | 'manual-paste' | 'validating' | 'complete'

interface Props {
  onClose: () => void
}

const STEPS: Step[] = ['method', 'auto-auth', 'validating', 'complete']
const STEP_LABELS = ['Method', 'Authenticate', 'Validate', 'Done']

export function NotebookLmAuthModal({ onClose }: Props) {
  const [step, setStep] = useState<Step>('method')
  const [method, setMethod] = useState<'auto' | 'manual'>('auto')
  const [cookieText, setCookieText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validationSteps, setValidationSteps] = useState<{ label: string; done: boolean }[]>([])
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const getStepIndex = (s: Step): number => {
    if (s === 'manual-paste') return 1 // Same visual position as auto-auth
    return STEPS.indexOf(s)
  }

  const currentStepIndex = getStepIndex(step)

  // ─── Step 1: Method Selection ───────────────────────────────────────

  const handleContinue = () => {
    if (method === 'auto') {
      startAutoAuth()
    } else {
      setStep('manual-paste')
    }
  }

  // ─── Step 2a: Auto Chrome Auth ──────────────────────────────────────

  const startAutoAuth = async () => {
    setStep('auto-auth')
    setError(null)

    try {
      await api.settings.notebooklm.startAuth()

      // Start polling for completion
      pollRef.current = setInterval(async () => {
        try {
          const result = await api.settings.notebooklm.pollAuth()
          if (result.completed) {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null
            startValidation()
          } else if (result.error) {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null
            setError(result.error)
          }
        } catch {
          // Polling error, keep trying
        }
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start auth')
    }
  }

  // ─── Step 2b: Manual Cookie Paste ───────────────────────────────────

  const handlePasteSubmit = async () => {
    if (!cookieText.trim()) {
      setError('Please paste your cookie string')
      return
    }

    setError(null)

    try {
      const result = await api.settings.notebooklm.pasteCookies(cookieText.trim())
      if (result.saved) {
        startValidation()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save cookies')
    }
  }

  // ─── Step 3: Validation ─────────────────────────────────────────────

  const startValidation = async () => {
    setStep('validating')
    setError(null)

    const steps = [
      { label: 'Disconnecting old session...', done: false },
      { label: 'Connecting with new cookies...', done: false },
      { label: 'Verifying authentication...', done: false },
    ]
    setValidationSteps([...steps])

    try {
      // Step 1: reconnect
      steps[0] = { label: steps[0]!.label, done: true }
      setValidationSteps([...steps])

      const result = await api.settings.notebooklm.reconnect()

      steps[1] = { label: steps[1]!.label, done: true }
      setValidationSteps([...steps])

      if (result.connected) {
        steps[2] = { label: `Authentication verified (${result.tools} tools available)`, done: true }
        setValidationSteps([...steps])
        setStep('complete')
      } else {
        setError(result.error || 'Connection failed — cookies may be invalid')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reconnection failed')
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Re-authenticate NotebookLM
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  i === currentStepIndex
                    ? 'bg-blue-600 text-white'
                    : i < currentStepIndex
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
              >
                {i < currentStepIndex ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  i + 1
                )}
              </div>
              <span className={`text-xs ${
                i === currentStepIndex
                  ? 'text-gray-900 dark:text-white font-medium'
                  : 'text-gray-400 dark:text-gray-500'
              }`}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && (
                <div className={`w-6 h-px ${
                  i < currentStepIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Step 1: Method */}
          {step === 'method' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your NotebookLM session has expired. Choose how to re-authenticate:
              </p>

              <label
                className={`block p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  method === 'auto'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="method"
                  value="auto"
                  checked={method === 'auto'}
                  onChange={() => setMethod('auto')}
                  className="sr-only"
                />
                <div className="flex items-start gap-3">
                  <Chrome className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Automatic (Recommended)
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Opens Chrome — log into Google, visit NotebookLM, and cookies are
                      extracted automatically.
                    </p>
                  </div>
                </div>
              </label>

              <label
                className={`block p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  method === 'manual'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  name="method"
                  value="manual"
                  checked={method === 'manual'}
                  onChange={() => setMethod('manual')}
                  className="sr-only"
                />
                <div className="flex items-start gap-3">
                  <ClipboardPaste className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Manual Cookie Paste
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Copy cookies from browser DevTools and paste them here.
                      Use if Chrome auto-launch doesn't work.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Step 2a: Auto Auth */}
          {step === 'auto-auth' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                A Chrome window should have opened. Follow these steps:
              </p>

              <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-2">
                  <span className="font-medium text-blue-600 dark:text-blue-400">1.</span>
                  Log into your Google account (if needed)
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-blue-600 dark:text-blue-400">2.</span>
                  Navigate to <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">notebooklm.google.com</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-medium text-blue-600 dark:text-blue-400">3.</span>
                  Wait for the page to fully load
                </li>
              </ol>

              {!error && (
                <div className="flex items-center gap-3 py-4">
                  <KaijuLoader size="sm" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Waiting for authentication...
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Step 2b: Manual Paste */}
          {step === 'manual-paste' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p className="font-medium text-gray-900 dark:text-white">Option A: DevTools</p>
                <ol className="space-y-1 pl-4 list-decimal">
                  <li>Open Chrome and go to <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">notebooklm.google.com</span></li>
                  <li>Press <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">F12</kbd> to open DevTools</li>
                  <li>Go to <strong>Application</strong> tab, then <strong>Cookies</strong></li>
                  <li>Click on <strong>notebooklm.google.com</strong></li>
                  <li>Select all cookies and copy their values</li>
                </ol>

                <p className="font-medium text-gray-900 dark:text-white pt-2">Option B: Console</p>
                <p>Paste this in the Console tab (F12):</p>
                <code className="block px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
                  document.cookie
                </code>
                <p>Copy the output and paste below:</p>
              </div>

              <textarea
                value={cookieText}
                onChange={(e) => setCookieText(e.target.value)}
                placeholder="SID=abc123; HSID=def456; SSID=ghi789; ..."
                className="w-full h-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm font-mono text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
          )}

          {/* Step 3: Validating */}
          {step === 'validating' && (
            <div className="space-y-3">
              {validationSteps.map((vs, i) => (
                <div key={i} className="flex items-center gap-3">
                  {vs.done ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : error ? (
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  ) : (
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                  )}
                  <span className={`text-sm ${
                    vs.done
                      ? 'text-green-700 dark:text-green-400'
                      : error
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {vs.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                NotebookLM is connected!
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your session has been refreshed. Notebook operations (queries, sources, studio)
                are now available across all customers.
              </p>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {step === 'method' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Continue
              </button>
            </>
          )}

          {step === 'auto-auth' && (
            <>
              <button
                onClick={() => {
                  if (pollRef.current) clearInterval(pollRef.current)
                  pollRef.current = null
                  setStep('method')
                  setError(null)
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (pollRef.current) clearInterval(pollRef.current)
                  pollRef.current = null
                  setMethod('manual')
                  setStep('manual-paste')
                  setError(null)
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Switch to Manual
              </button>
            </>
          )}

          {step === 'manual-paste' && (
            <>
              <button
                onClick={() => {
                  setStep('method')
                  setError(null)
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={handlePasteSubmit}
                disabled={!cookieText.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Submit
              </button>
            </>
          )}

          {step === 'validating' && error && (
            <button
              onClick={startValidation}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          )}

          {step === 'complete' && (
            <button
              onClick={() => {
                notify.success('NotebookLM re-authenticated')
                onClose()
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
